import { DotMatrix } from '@clickguard/design-system'
import { useAuth0 } from '@auth0/auth0-react'
import { lazy, Suspense, useEffect, useState } from 'react'
import { Route, Routes, useLocation, useSearchParams } from 'react-router-dom'
import { ApiClient } from 'services/api-client'
import { usePostHogWorkspaceGroup } from 'utils/posthog'
import { useUsageIdentity } from './hooks/use-usage-identity'
import { useShallow } from 'zustand/react/shallow'
import { Auth0Config } from './config'
import { toast } from 'react-toastify'
import {
	useAccountStore,
	useAdminStore,
	useAssetStore,
	useBillingStore,
	useUiStore,
	useUserStore
} from 'store'
import { ThemeFix } from 'theme-fix'
import { ChatService } from './services/chat'
import { publicRoutes, responsiveRoutes } from './utils/public-routes'
import { MainPage } from './pages'
import { contentReaderPaths, StandaloneContentReaderPage } from './pages/admin/content/content-page'
import { FacebookOAuthPage } from 'pages/oauth/facebook'
import { BingOAuthPage } from './pages/oauth/bing'
import { GoogleOAuthPage } from './pages/oauth/google'
import { ShopifyOAuthPage } from './pages/oauth/shopify'
import { ShopifyClaimPage } from './pages/oauth/shopify-claim'
import { ErrorPage } from 'pages/error'
import { PasswordReset } from 'pages/public-pages/password-reset/password-reset'
import { JoinWorkspacePage } from 'pages/user/join-workspace/join-workspace'
import { StartStandard } from './pages/start/standard/v2'
import { ConfirmUserPage } from './pages/user/confirm/confirm'
import { ChangePassword } from './pages/public-pages/password-reset/change-password'
import { JoinAdminTeam } from './pages/admin/team/accept-team-invite'
import { EmailVerifiedView } from './pages/public-pages/verify-email-page/verify-email-page'
import { OnboardingOfferLayout } from 'components/black-friday-banner/onboarding-offer-layout'
import { GModal } from './components/basic-blocks/g-modal'
import { CiLaptop } from 'react-icons/ci'
import {
	setMarketingParams,
	clearMarketingParams
} from './utils/marketing-params'
import { useNavigateWithParams } from './hooks/use-navigate-with-params'
import { useDarkModeFlag } from './hooks/use-dark-mode-flag'
import {
	getPendingInviteCode,
	setPendingInviteCode,
	clearPendingInvite,
	isValidInviteCode
} from './utils/invite-storage'
import { clearFiltersFromStorage } from './utils/filter-presistence'
import {
	getPendingTrialExtension,
	setPendingTrialExtension,
	clearPendingTrialExtension,
	clearStalePendingTrialExtension
} from './utils/trial-extension-storage'
import { SetupV2 } from 'pages/setup/v2/setup-v2'
import { TrialExtensionDeeplinkModal } from 'components/trial-extension-deeplink'
import '@clickguard/design-system/styles.css'
// Must stay directly below the design-system styles import — see the comment
// inside; it repairs the default border color the DS preflight resets.
import './preflight-border-guard.css'

const SignUpPage = () => {
	const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0()
	useEffect(() => {
		if (isLoading) return

		const urlParams = new URLSearchParams(window.location.search)
		if (urlParams.get('code') || urlParams.get('state')) {
			return
		}

		const allParams: Record<string, string> = {}

		urlParams.forEach((value, key) => {
			if (key !== 'code' && key !== 'state') {
				allParams[key] = value
			}
		})

		const auth0Params: any = {
			screen_hint: 'signup',
			appState: {
				returnTo: '/',
				marketingParams: allParams
			}
		}

		Object.keys(allParams).forEach((key) => {
			auth0Params[key] = allParams[key]
		})

		loginWithRedirect(auth0Params)
	}, [isAuthenticated, isLoading, loginWithRedirect])
	return <></>
}

// Public Analytics report link (DEV-2377): mounted HERE, beside the other
// auth-free pages, so it never enters MainPage — which wraps everything it
// routes in MainLayout (Sidebar + Navbar) and fetches the account list. The
// recipient must see a blank page with a loader and nothing else. Lazy so
// react-pdf stays out of the main bundle.
const ReportRenderPage = lazy(() =>
	import('./pages/report-render/report-render-page').then((m) => ({
		default: m.ReportRenderPage
	}))
)

const LoginPage = () => {
	const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0()
	useEffect(() => {
		if (isLoading) return

		const urlParams = new URLSearchParams(window.location.search)
		if (urlParams.get('code') || urlParams.get('state')) {
			return
		}

		const allParams: Record<string, string> = {}

		urlParams.forEach((value, key) => {
			if (key !== 'code' && key !== 'state') {
				allParams[key] = value
			}
		})

		// Store marketing params in session storage
		setMarketingParams(allParams)

		const auth0Params: any = {
			screen_hint: 'login',
			appState: {
				returnTo: isAuthenticated
					? '/'
					: window.location.pathname + window.location.search,
				marketingParams: allParams
			}
		}

		Object.keys(allParams).forEach((key) => {
			auth0Params[key] = allParams[key]
		})

		loginWithRedirect(auth0Params)
	}, [isAuthenticated, isLoading, loginWithRedirect])
	return <></>
}

export const App = () => {
	usePostHogWorkspaceGroup()
	useUsageIdentity()
	const {
		isLoading: auth0Loading,
		error: auth0Error,
		isAuthenticated,
		loginWithRedirect,
		getAccessTokenSilently,
		logout
	} = useAuth0()

	const navigate = useNavigateWithParams()
	const currentLocation = useLocation()
	const [search] = useSearchParams()

	const { modalContent, setActivePage, context } = useUiStore(
		useShallow((state) => ({
			modalContent: state.modalContent,
			setActivePage: state.setActivePage,
			context: state.context
		}))
	)
	const { setImpersonation, imperId, clearImpersonation } = useAdminStore(
		useShallow((state) => ({
			setImpersonation: state.setImpersonation,
			imperId: state.imperId,
			clearImpersonation: state.clearImpersonation
		}))
	)
	const { initUser } = useUserStore((state) => ({
		initUser: state.initUser,
		visualPreferences: state.visualPreferences,
		updateVisualPreferences: state.updateVisualPreferences
	}))
	const { getAllAccounts, wantsToSeeDemoData, setAccount, account, accounts } =
		useAccountStore(
			useShallow((state) => ({
				getAllAccounts: state.getAllAccounts,
				setAccount: state.setAccount,
				wantsToSeeDemoData: state.wantsToSeeDemoData,
				account: state.account,
				accounts: state.accounts
			}))
		)
	const { getAllAssets, setAsset } = useAssetStore(
		useShallow((state) => ({
			getAllAssets: state.getAllAssets,
			setAsset: state.setAsset
		}))
	)
	const { loadOfferRequests, loadOffers } = useBillingStore(
		useShallow((state) => ({
			loadOfferRequests: state.loadOfferRequests,
			loadOffers: state.loadOffers
		}))
	)

	const [loading, setLoading] = useState(true)

	// Syncs 'dark_mode_enabled' into the theme resolver so the stored theme
	// preference defaults to light while the flag is off, on every route.
	useDarkModeFlag()

	const isPublicRoute = publicRoutes.find((route) =>
		currentLocation.pathname.startsWith(route)
	)
	const isResponsiveRoute =
		currentLocation.pathname.includes('setup') ||
		currentLocation.pathname.includes('start/standard') ||
		!!responsiveRoutes.find((route) =>
			currentLocation.pathname.startsWith(route)
		)
	async function init() {
		try {
			setLoading(true)

			const isJoinWorkspaceRoute =
				currentLocation.pathname.includes('/join-workspace')

			if (isPublicRoute || (isJoinWorkspaceRoute && !isAuthenticated)) {
				setLoading(false)
				return
			}

			const tokenOptions = {
				audience: Auth0Config.audience,
				scope: Auth0Config.scope
			}
			let token: string
			try {
				token = await getAccessTokenSilently(tokenOptions)
			} catch (tokenError: any) {
				// After redirect, token may not be in cache yet; retry once on transient errors
				const isTransient =
					tokenError?.message?.includes('timeout') ||
					tokenError?.message === 'Failed to fetch'
				if (isAuthenticated && isTransient) {
					await new Promise((r) => setTimeout(r, 500))
					token = await getAccessTokenSilently(tokenOptions)
				} else {
					throw tokenError
				}
			}
			ApiClient.setAuthToken(token)
			ApiClient.setLogoutListener(() => {
				clearFiltersFromStorage()
				clearMarketingParams()
				logout()
			})
			ApiClient.setUnverifiedEmailError(() => navigate('/emails/verify'))
			if (!isAuthenticated) {
				return
			}

			if (imperId) {
				setImpersonation(imperId)
				setAccount(null)
				setAsset(null)
			}

			const initializedUser = await initUser(search.get('ref'))

			const isOAuthRoute = currentLocation.pathname.startsWith('/oauth/')
			const urlParams = new URLSearchParams(window.location.search)
			const codeFromUrl = urlParams.get('code')

			const looksLikeInviteCode = isValidInviteCode(codeFromUrl)
			if (codeFromUrl && !isOAuthRoute && looksLikeInviteCode) {
				setPendingInviteCode(codeFromUrl)
			}

			if (!initializedUser.email_verified) {
				if (currentLocation.pathname !== '/emails/verify') {
					const inviteCode =
						!isOAuthRoute && looksLikeInviteCode
							? getPendingInviteCode() || codeFromUrl
							: null
					const verifyUrl = inviteCode
						? `/emails/verify?code=${inviteCode}`
						: '/emails/verify'
					navigate(verifyUrl)
				}
				setLoading(false)
				return
			}

			const fetchedAccounts = await getAllAccounts()
			const accountsList = fetchedAccounts ?? accounts

			if (
				accountsList.length > 0 &&
				!currentLocation.pathname.includes('/join-workspace')
			) {
				clearPendingInvite()
			}
			const finalPendingInviteCode = getPendingInviteCode()
			if (
				finalPendingInviteCode &&
				accountsList.length === 0 &&
				currentLocation.pathname !== '/join-workspace' &&
				!currentLocation.pathname.includes('/join-workspace') &&
				!isOAuthRoute
			) {
				navigate(`/join-workspace?code=${finalPendingInviteCode}`)
				setLoading(false)
				return
			}

			if (initializedUser.admin && currentLocation.pathname === '/') {
				navigate('/admin')
			}

			if (wantsToSeeDemoData) {
				setLoading(false)
				return
			}

			await Promise.all([
				getAllAccounts(),
				getAllAssets(),
				loadOfferRequests(),
				loadOffers()
			])

			clearStalePendingTrialExtension()

			const trialExtensionParam = search.get('trial_extension')
			const trialExtensionAccountId = search.get('account_id')

			if (trialExtensionParam === 'true' && trialExtensionAccountId) {
				const targetAccount = accountsList.find(
					(acc: any) => acc.id === trialExtensionAccountId
				)
				if (!targetAccount) {
					setPendingTrialExtension(trialExtensionAccountId)
					useUiStore
						.getState()
						.setTrialExtensionDeeplink(true, trialExtensionAccountId, true)
					navigate('/', { replace: true })
					setLoading(false)
					return
				}
				setAccount(targetAccount)
				useUiStore
					.getState()
					.setTrialExtensionDeeplink(true, trialExtensionAccountId)
				clearPendingTrialExtension()
				navigate('/', { replace: true })
			} else {
				const pendingAccountId = getPendingTrialExtension()
				if (pendingAccountId) {
					const targetAccount = accountsList.find(
						(acc: any) => acc.id === pendingAccountId
					)
					if (targetAccount) {
						setAccount(targetAccount)
						useUiStore
							.getState()
							.setTrialExtensionDeeplink(true, pendingAccountId)
					}
					clearPendingTrialExtension()
				}
			}

			setLoading(false)
		} catch (e: any) {
			if (e?.message === 'External interaction required') {
				toast.success('Success please Login again')
				setTimeout(() => {
					clearMarketingParams()
					logout()
				}, 1000)
			} else if (e.message !== 'Login required')
				toast.error('Error while initializing data')
			setLoading(false)
		}
	}

	useEffect(() => {
		if (auth0Loading) return
		init()
		return () => {
			setLoading(false)
		}
	}, [isAuthenticated, imperId, auth0Loading])

	useEffect(() => {
		if (!isAuthenticated || isPublicRoute) return
	}, [isAuthenticated, isPublicRoute])

	useEffect(() => {
		setActivePage(currentLocation.pathname)

		if (isPublicRoute) return

		currentLocation.pathname.includes('admin')
			? ChatService.hide()
			: ChatService.show()

		if (
			currentLocation.pathname.includes('/workspace') ||
			currentLocation.pathname.includes('/dashboard')
		) {
			clearPendingInvite()
		}
	}, [currentLocation.pathname])

	useEffect(() => {
		const handleBeforeUnload = () => {
			clearFiltersFromStorage()
		}

		window.addEventListener('beforeunload', handleBeforeUnload)

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload)
		}
	}, [])

	if (auth0Loading || loading) {
		return (
			<div className="h-96 flex flex-col justify-end items-center text-center space-y-4">
				<DotMatrix size="lg" />
				<span className="w-full text-center text-t-secondary">Loading...</span>
			</div>
		)
	}

	if (auth0Error) {
		return <ErrorPage error={auth0Error.message} />
	}

	const isJoinWorkspaceRoute =
		currentLocation.pathname.includes('/join-workspace')

	if (!isAuthenticated && !isPublicRoute && !isJoinWorkspaceRoute) {
		loginWithRedirect({
			appState: {
				returnTo: window.location.pathname + window.location.search
			}
		})
		return null
	}

	return (
		<Suspense fallback={<DotMatrix size="lg" />}>
			<div className="min-h-full">
				{context !== 'admin' && !isResponsiveRoute && (
					<div className="md:hidden bg-t-smoke min-h-screen bg-gradient-to-br md:p-8 p-6 from-g-purple via-g-cyan to-g-gray flex items-center justify-center">
						<div
							className="mt-4 mr-8 border rounded-full px-6 py-2 border-gray-500 absolute right-0 top-0 text-primary-500 cursor-pointer"
							onClick={() => {
								clearImpersonation()
								clearMarketingParams()
								logout()
							}}
						>
							<span className="text-t-default">Back to</span> Login
						</div>
						<div className="bg-white flex flex-col gap-2 w-full h-full items-center justify-center rounded-3xl p-6">
							<div className="pb-2">
								<CiLaptop className="w-12 h-12" />
							</div>
							<div className="text-xl text-center font-semibold">
								Works best on desktop
							</div>
							<div className="text-t-secondary text-center text-md">
								For the smoothest experience, our platform is available on
								desktop devices.
							</div>
						</div>
					</div>
				)}
				<div
					className={
						context !== 'admin' && !isResponsiveRoute ? 'hidden md:block' : ''
					}
				>
					<GModal {...modalContent} />
					<TrialExtensionDeeplinkModal />
					<Routes>
						{contentReaderPaths.map((path) => (
							<Route key={path} path={path} element={<StandaloneContentReaderPage />} />
						))}
						<Route
							path="/emails/verify"
							element={
								<OnboardingOfferLayout>
									<EmailVerifiedView />
								</OnboardingOfferLayout>
							}
						/>
						<Route path="/change-password" element={<ChangePassword />} />
						<Route path="/reset-password" element={<PasswordReset />} />
						<Route path="/confirmation" element={<ConfirmUserPage />} />
						<Route path="/join-workspace" element={<JoinWorkspacePage />} />
						<Route path="admin/join-team" element={<JoinAdminTeam />} />

						<Route path="/start/standard" element={<StartStandard />} />
						<Route
							path="/start/standard/:accountId"
							element={<StartStandard />}
						/>
						<Route path="/setup/:accountId" element={<SetupV2 />} />
						<Route path="/setup/:accountId/:assetId" element={<SetupV2 />} />
						<Route
							path="/setup/:accountId/:assetId/tracking"
							element={<SetupV2 />}
						/>
						<Route path="/sign-up" element={<SignUpPage />} />
						<Route path="/login" element={<LoginPage />} />

						<Route path="/oauth/google" element={<GoogleOAuthPage />} />
						<Route path="/oauth/facebook" element={<FacebookOAuthPage />} />
						<Route path="/oauth/bing" element={<BingOAuthPage />} />
						<Route path="/oauth/shopify/claim" element={<ShopifyClaimPage />} />
						<Route path="/oauth/shopify" element={<ShopifyOAuthPage />} />

						<Route
							path="/report/:id"
							element={
								<Suspense fallback={null}>
									<ReportRenderPage />
								</Suspense>
							}
						/>

						<Route path="*" element={<MainPage />} />
					</Routes>
				</div>
				<ThemeFix />
			</div>
		</Suspense>
	)
}
